import { useAuth0 } from "@auth0/auth0-react";

export interface GenerativeModelTestImageDTO {
    id?: string;
    sort_order: number;
    score: number | null;
    generative_output_id?: string;
    generative_model_variation_id: string;
    image_path: string;
    group_description: string;
    comment?: string;
}

export interface GenerativeModelTestImageResult {
    score: number;
    comment?: string;
}

export type GenerativeModelTestScenarioStatus = "PENDING" | "IN_PROGRESS" | "COMPLETED";

export interface GenerativeModelTestResult {
    variation_scores?: any
    best_variation?: string;
    best_score?: number;
    is_successful?: boolean;
}

export interface GenerativeModelTestScenarioDTO {
    id?: string;
    status: GenerativeModelTestScenarioStatus;
    generative_model_id?: string;
    entity_type?: string;
    entity_id?: string;
    result?: GenerativeModelTestResult
    images: GenerativeModelTestImageDTO[];
    updated_at?: string;
    created_at?: string;
}

const API_URL = process.env.REACT_APP_API_URL;

export function useGenerativeModelTestScenarioAPI() {
    const { getAccessTokenSilently } = useAuth0();

    const listScenarios = async (
        generativeModelId?: string,
        status?: string
    ): Promise<GenerativeModelTestScenarioDTO[]> => {
        const accessToken = await getAccessTokenSilently({});
        const params = new URLSearchParams();
        if (generativeModelId) params.append("generativeModelId", generativeModelId);
        if (status) params.append("status", status);

        const url = `${API_URL}/generative-model-test-scenario?${params.toString()}`;
        const response = await fetch(url, {
            headers: { authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) throw new Error("Failed to fetch scenarios");
        return response.json();
    };

    const getScenarioById = async (
        scenarioId: string
    ): Promise<GenerativeModelTestScenarioDTO> => {
        const accessToken = await getAccessTokenSilently({});
        const url = `${API_URL}/generative-model-test-scenario/${scenarioId}`;
        const response = await fetch(url, {
            headers: { authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) throw new Error("Failed to fetch scenario");
        return response.json();
    };

    const recordEvaluation = async (
        scenarioId: string,
        imageId: string,
        result: GenerativeModelTestImageResult
    ): Promise<GenerativeModelTestScenarioDTO> => {
        const accessToken = await getAccessTokenSilently({});
        const url = `${API_URL}/generative-model-test-scenario/${scenarioId}/${imageId}`;
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(result),
        });
        if (!response.ok) throw new Error("Failed to record evaluation");
        return response.json();
    };
    const resetEvaluation = async (
        scenarioId: string
    ): Promise<GenerativeModelTestScenarioDTO> => {
        const accessToken = await getAccessTokenSilently({});
        const url = `${API_URL}/generative-model-test-scenario/${scenarioId}/reset`;
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                authorization: `Bearer ${accessToken}`,
            }
        });
        if (!response.ok) {
            var errorMessage = "Failed to reset vault evaluation";
            try {
                const errorData = await response.json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch (e) {
            }
            throw new Error(errorMessage);
        }
        return response.json();
    };
    const deleteScenario = async (
        scenarioId: string
    ): Promise<GenerativeModelTestScenarioDTO> => {
        const accessToken = await getAccessTokenSilently({});
        const url = `${API_URL}/generative-model-test-scenario/${scenarioId}`;
        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                authorization: `Bearer ${accessToken}`,
            }
        });
        if (!response.ok) {
            var errorMessage = "Failed to delete vault evaluation";
            try {
                const errorData = await response.json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch (e) {
            }
            throw new Error(errorMessage);
        }
        return response.json();
    };
    const createScenario = async (
        genModelId: string
    ): Promise<void> => {
        const accessToken = await getAccessTokenSilently({});
        const url = `${API_URL}/generative-model-test-scenario/model/${genModelId}`;
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                authorization: `Bearer ${accessToken}`,
            }
        });
        if (!response.ok) {
            var errorMessage = "Failed to create vault evaluation";
            try {
                const errorData = await response.json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch (e) {
            }
            throw new Error(errorMessage);
        }
    };
    const emailEvaluation = async (
        scenarioId: string
    ): Promise<GenerativeModelTestScenarioDTO> => {
        const accessToken = await getAccessTokenSilently({});
        const url = `${API_URL}/generative-model-test-scenario/${scenarioId}/email`;
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                authorization: `Bearer ${accessToken}`,
            }
        });
        if (!response.ok) {
            var errorMessage = "Failed to send evaluation email";
            try {
                const errorData = await response.json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch (e) {
            }
            throw new Error(errorMessage);
        }
        return response.json();
    };

    const cancelEvaluation = async (
        scenarioId: string
    ): Promise<void> => {
        const accessToken = await getAccessTokenSilently({});
        const url = `${API_URL}/generative-model-test-scenario/${scenarioId}/cancel`;
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                authorization: `Bearer ${accessToken}`,
            }
        });
        if (!response.ok) {
            var errorMessage = "Failed to cancel vault evaluation";
            try {
                const errorData = await response.json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch (e) {
            }
            throw new Error(errorMessage);
        }
    };

    return {
        listScenarios,
        getScenarioById,
        recordEvaluation,
        resetEvaluation,
        emailEvaluation,
        deleteScenario,
        createScenario,
        cancelEvaluation
    };
}